import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../../../components/Header";
import { useAuth } from "../../../context/AuthContext";
import { doc, getDoc } from "firebase/firestore";
import { signOut } from "firebase/auth";
import { db, auth } from "../../../firebase";
import { registerFcmToken } from "../../../services/fcmService";

function LawyerSignIn() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const { login, lawyerGoogleLogin } = useAuth();
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const { globalData, setGlobalData, globalUser } = useAuth();

  // Role chosen on the onboarding sliding selector ("lawyer" | "student").
  const isStudent = localStorage.getItem("userRole") === "student";

  const saveFcmToken = (user, userType) => registerFcmToken(user, userType);

  async function handleGoogleSignIn() {
    try {
      setIsAuthenticating(true);
      const result = await lawyerGoogleLogin("login");
      localStorage.setItem("accessTokenGoogle", result.user.accessToken);
      localStorage.setItem("user", JSON.stringify(result.user));
      localStorage.setItem("userId", result.user.uid);

      if (result.success) {
         saveFcmToken(result.user, "lawyers");
        console.log(
          "Google sign-in successful, redirecting to:",
          result.redirectTo
        );
        navigate(result.redirectTo);
      }
    } catch (err) {
      console.error("Google sign-in error:", err);
      setError(err.message || "Failed to sign in with Google");
    } finally {
      setIsAuthenticating(false);
    }
  }

  async function handleAuthenticate() {
    if (
      !email ||
      !email.includes("@") ||
      !password ||
      password.length < 6 ||
      isAuthenticating
    ) {
      return;
    }

    try {
      setIsAuthenticating(true);
      const result = await login(email, password);
      console.log("access token ", result.user.accessToken);
      console.log("user ", result.user);
      console.log("line 50", result);

      localStorage.setItem("accessTokenGoogle", result.user.accessToken);
      localStorage.setItem("user", JSON.stringify(result.user));
      localStorage.setItem("userId", result.user.uid);
      localStorage.setItem("userEmail", result.user.email);

      if (result.user) {
        // Fetch user data from Firestore to check if mobile number exists
        const userRef = doc(db, "lawyers", result.user.uid);
        const userDoc = await getDoc(userRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          console.log("User data from Firestore:", userData);

          // A Lawyer account and a Law Student account share the "lawyers"
          // collection — make sure the role picked on the onboarding selector
          // matches the role this account was actually registered with.
          const selectedRole =
            localStorage.getItem("userRole") === "student" ? "student" : "lawyer";
          const storedRole =
            userData.userCateogry === "Law student" ? "student" : "lawyer";
          if (selectedRole !== storedRole) {
            await signOut(auth);
            setError(
              storedRole === "student"
                ? "This account is registered as a Law Student. Please go back and select 'Law Student' to sign in."
                : "This account is registered as a Lawyer. Please go back and select 'Lawyer' to sign in.",
            );
            return;
          }
          // Keep the stored role authoritative for the rest of the app.
          localStorage.setItem("userRole", storedRole);

          // Check if mobile number exists
          if (!userData.mobileNumber && !userData.mobileWithCountryCode) {
            console.log("Mobile number not found, redirecting to phone verification");
            navigate("/portal/phoneVerification");
          } else {
            console.log("Login successful, redirecting to Dashboard");
            saveFcmToken(result.user, "lawyers");
            navigate("/portal/Dashboard");
          }
        } else {
          console.error("User document not found in Firestore");
          setError("User profile not found. Please contact support.");
        }
      }
    } catch (err) {
      console.log(err.message);

      if (err.code === "auth/user-not-found") {
        setError("User not found, please sign up");
      } else if (err.code === "auth/wrong-password") {
        setError("Incorrect password, please try again");
      } else if (err.code === "auth/invalid-email") {
        setError("Invalid email address");
      } else if (err.code === "auth/too-many-requests") {
        setError("Too many requests, please try again later");
      } else if (err.code === "auth/operation-not-allowed") {
        setError("Operation not allowed, please contact support");
      } else if (err.code === "auth/invalid-credential") {
        setError("Invalid credentials, please try again");
      } else {
        setError(err.message || "Failed to sign in");
      }
    } finally {
      setIsAuthenticating(false);
    }
  }

  return (
    <>
      <Header />
      <div className="min-h-screen overflow-hidden py-10 sm:py-0  flex flex-col bg-[#060223] items-center justify-center relative">
        <img
          src="../ellipse-top.png"
          className="absolute rotate-27 right-[-25vw] top-[-20vh] sm:right-[-150px] sm:top-[-200px] "
          alt=""
        />
        <img
          src="../ellipse-bottom.png"
          className="absolute sm:bottom-[-300px] bottom-[-20vh]"
          alt=""
        />
        <img
          src="../JURIDENT.png"
          className="absolute opacity-0 sm:opacity-[1] left-5"
          alt=""
        />
        <img
          src="../Jurident-Logo-bottom.png"
          className="absolute opacity-0 sm:opacity-[1] right-5 bottom-5"
          alt=""
        />
        {/* Main Container */}
        <div className="sm:grid sm:grid-cols-3 gap-10 pt-14 items-center justify-center p-4 z-10">
          {/* Lawyer Image */}
          <div className="flex flex-col items-center justify-center">
            <img src="../LawyerImage.png" alt="Lawyer" className="" />
          </div>

          {/* Sign-Up Form */}
          <div className="col-span-2 p-5 max-w-xs z-101 rounded-lg w-full">
            <h2 className="text-2xl font-semibold text-[#C99F4A] text-center">
              Sign In
            </h2>
            <p className="text-center text-gray-300 mt-1 text-sm">
              Become a legal eagle! Login and spread your wings in the
              courtroom.
            </p>

            {/* Join as Lawyer */}
            <h3 className="text-md font-medium text-[#C99F4A] text-center mt-3">
              Join as {isStudent ? "Law Student" : "Lawyer"}
            </h3>
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded mt-4 text-sm">
                {error}
              </div>
            )}

            <div className="flex justify-center gap-4 mt-4">
              <button
                onClick={handleGoogleSignIn}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-200 text-black rounded-lg w-36"
              >
                <i className="fa-brands fa-google"></i> Google
              </button>
            </div>

            <div className="flex items-center my-3">
              <div className="flex-1 border-t border-gray-600"></div>
              <p className="px-2 text-gray-400 text-sm">Or</p>
              <div className="flex-1 border-t border-gray-600"></div>
            </div>

            <div className="space-y-3 ">
              <input
                type="text"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                }}
                placeholder="Email"
                className="w-full p-2.5 bg-white text-black rounded-lg focus:outline-none text-sm"
              />
              <div className="relative">
                <input
                  id="password"
                  value={password}
                  type="password"
                  placeholder="Password"
                  onChange={(e) => {
                    setPassword(e.target.value);
                  }}
                  className="w-full p-2.5 bg-white text-black rounded-lg focus:outline-none text-sm"
                />
                <button
                  type="button"
                  onClick={() => {
                    let eye = document.getElementById("password-eye");
                    let togglepassword = document.getElementById("password");
                    if (togglepassword.type === "password") {
                      togglepassword.type = "text";
                      eye.classList.remove("fa-eye");
                      eye.classList.add("fa-eye-slash");
                    } else {
                      togglepassword.type = "password";
                      eye.classList.add("fa-eye");
                      eye.classList.remove("fa-eye-slash");
                    }
                  }}
                  className="absolute right-4 top-3 text-gray-400 cursor-pointer"
                >
                  <i id="password-eye" className="fa-solid fa-eye"></i>
                </button>
              </div>
              <div className="flex items-center gap-2 text-sm text-[#C99F4A]">
                <input type="checkbox" /> Keep Me Logged In
              </div>
              <button
                onClick={handleAuthenticate}
                className="w-full bg-blue-600 p-2.5 rounded-lg text-white font-medium hover:bg-blue-700 text-sm"
              >
                {isAuthenticating ? "Authenticating..." : "Sign In"}
              </button>
            </div>

            <p className="text-center text-[#C99F4A] cursor-pointer mt-3 text-sm">
              <Link to="../ForgotPassword">Forgot Password</Link>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

export default LawyerSignIn;
